import { skipToken, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

/**
 * Rascunho da mensagem de cada conversa. Mora no cache do QueryClient, FORA da chave da sessão:
 * volta ao reabrir a conversa e some no logout ou na troca de conta (nada de localStorage nem de
 * variável de módulo, que levariam o texto de uma pessoa para a seguinte da mesma aba).
 */
export const draftKey = (conversationId: string) => ['draft', 'message', conversationId] as const;

export function readDraft(queryClient: QueryClient, conversationId: string): string {
  return queryClient.getQueryData<string>(draftKey(conversationId)) ?? '';
}

/**
 * Texto devolvido ao compositor sem apagar o que a pessoa já tinha começado a digitar: o devolvido
 * vem antes, separado por uma linha em branco.
 */
export const prependToDraft = (text: string, draft: string): string =>
  draft.trim() ? `${text}\n\n${draft}` : text;

/**
 * Escreve o rascunho de FORA da tela (o envio que o servidor recusou devolve o texto). Um
 * compositor aberto acompanha a mudança (ver `useDraft`); fechado, o texto espera no cache.
 */
export function writeDraft(queryClient: QueryClient, conversationId: string, value: string): void {
  queryClient.setQueryData<string>(draftKey(conversationId), value);
}

/**
 * Texto do compositor + gravação no cache a cada alteração. O texto vive também em estado do
 * React porque o cache avisa os componentes com atraso (um `setTimeout`), e um campo controlado
 * que recebe o valor atrasado perde teclas.
 *
 * O caminho contrário (cache → estado) cobre quem escreve o rascunho de fora (`writeDraft`): a cada
 * mudança do cache o estado passa a valer o valor MAIS NOVO dele. Como cada tecla grava no cache
 * antes de o aviso chegar, esse valor é sempre o que a pessoa acabou de digitar e nenhuma tecla se perde.
 */
export function useDraft(conversationId: string): [string, (value: string) => void] {
  const queryClient = useQueryClient();
  // O observador só mantém o rascunho vivo no cache (gcTime infinito) enquanto a pessoa navega
  // por outras conversas; o valor lido aqui é o do estado.
  useQuery<string>({
    queryKey: draftKey(conversationId),
    queryFn: skipToken,
    initialData: '',
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const [text, setText] = useState(() => readDraft(queryClient, conversationId));

  useEffect(() => {
    const wanted = draftKey(conversationId).join('/');
    return queryClient.getQueryCache().subscribe((event) => {
      // `removed`: o logout/troca de conta apagou o rascunho com a tela ainda montada.
      if (
        (event.type === 'updated' || event.type === 'removed') &&
        event.query.queryKey.join('/') === wanted
      ) {
        setText(readDraft(queryClient, conversationId));
      }
    });
  }, [queryClient, conversationId]);

  const update = useCallback(
    (value: string) => {
      setText(value);
      queryClient.setQueryData<string>(draftKey(conversationId), value);
    },
    [queryClient, conversationId],
  );
  return [text, update];
}
